import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "350px" };
const defaultCenter = { lat: -17.8274, lng: 31.0435 };

const toNumber = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toLatLng = (obj) => {
  if (!obj) return null;
  const lat = toNumber(obj.lat ?? obj.latitude);
  const lng = toNumber(obj.lng ?? obj.longitude);
  if (lat == null || lng == null) return null;
  return { lat, lng };
};

const RideSharePassengerTripCardRoutePreview = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const mapRef = useRef(null);

  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [midpoints, setMidpoints] = useState([]);
  const [driverCurrentLocation, setDriverCurrentLocation] = useState(null);
  
  // Track previous driver location to detect changes
  const prevDriverLocationRef = useRef(null);

  const [directions, setDirections] = useState(null);

  const center = useMemo(() => {
  // Always center on driver current location when available
  if (driverCurrentLocation) return driverCurrentLocation;
  // Fallback to origin or destination if no driver location
  if (origin) return origin;
  return destination || defaultCenter;
}, [driverCurrentLocation, origin, destination]);

  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const raw = event?.data;
        if (!raw) return;
        const payload = typeof raw === "string" ? JSON.parse(raw) : raw;

        const trip = payload?.trip ?? payload;
        
        console.log('[Maps Component] Received payload:', JSON.stringify(payload, null, 2));
        console.log('[Maps Component] Trip waypoints:', trip?.waypoints);
        console.log('[Maps Component] Trip midpoints:', trip?.midpoints);
        console.log('[Maps Component] Driver current location:', trip?.driverCurrentLocation);

        const o = toLatLng({ lat: trip?.origin_lat, lng: trip?.origin_lng });
        const d = toLatLng({ lat: trip?.destination_lat, lng: trip?.destination_lng });

        const wps = (Array.isArray(trip?.waypoints) ? trip.waypoints : [])
          .map((w) => {
            const latLng = toLatLng({ lat: w?.lat, lng: w?.lng });
            return latLng ? { ...latLng, label: w?.label } : null;
          })
          .filter(Boolean);

        const mids = (Array.isArray(trip?.midpoints) ? trip.midpoints : [])
          .map((m) => toLatLng({ lat: m?.lat, lng: m?.lng }))
          .filter(Boolean);

        // Extract driver current location if available
        const driverLoc = trip?.driverCurrentLocation ? toLatLng(trip.driverCurrentLocation) : null;
        
        // Check if driver location actually changed
        const driverLocationChanged = driverLoc && (
          !prevDriverLocationRef.current ||
          driverLoc.lat !== prevDriverLocationRef.current.lat ||
          driverLoc.lng !== prevDriverLocationRef.current.lng
        );
        
        if (driverLocationChanged) {
          console.log('[Maps Component] Driver location changed, updating map');
          prevDriverLocationRef.current = driverLoc;
        }
        
        console.log('[Maps Component] Processed driver current location:', driverLoc);

        if (o) setOrigin(o);
        if (d) setDestination(d);
        setWaypoints(wps);
        setMidpoints(mids);
        
        // Only update driver location if it changed
        if (driverLocationChanged) {
          setDriverCurrentLocation(driverLoc);
        }
      } catch (e) {
        console.error('[Maps Component] Error processing message:', e);
        return;
      }
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !window.google) return;
    
    // Build route points: driver current location → origin → waypoints → midpoints → destination
    const routePoints = [];
    
    // Add driver current location as starting point if available
    if (driverCurrentLocation) {
      routePoints.push(driverCurrentLocation);
    }
    
    // Add trip origin
    if (origin) {
      routePoints.push(origin);
    }
    
    // Add passenger waypoints
    routePoints.push(...waypoints);
    
    // Add midpoints
    routePoints.push(...midpoints);
    
    // Add destination
    if (destination) {
      routePoints.push(destination);
    }
    
    const limited = routePoints.slice(0, 10);
    if (limited.length < 2) return;

    const service = new window.google.maps.DirectionsService();
    const o = limited[0];
    const d = limited[limited.length - 1];
    const wp = limited.slice(1, limited.length - 1).map((p) => ({ location: p, stopover: true }));

    service.route(
      {
        origin: o,
        destination: d,
        waypoints: wp,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          setDirections(null);
        }
      }
    );
  }, [isLoaded, origin, destination, waypoints, midpoints, driverCurrentLocation]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "auto" }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={12}
        center={center}
        onLoad={(map) => (mapRef.current = map)}
        options={{
          gestureHandling: 'auto', // Enable gestures
          scrollwheel: true,       // Enable scroll wheel zoom
          zoomControl: true,       // Show zoom controls
          panControl: true,         // Enable panning
          draggable: true,          // Enable dragging
          fullscreenControl: false, // Hide fullscreen control for small preview
          mapTypeControl: false,   // Hide map type control for small preview
          streetViewControl: false, // Hide street view control
        }}
      >
        {driverCurrentLocation && (
          <>
            {/* Glow effect - larger, semi-transparent circle */}
            <Marker 
              position={driverCurrentLocation} 
              title="Driver Current Location" 
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 24,
                fillColor: "#9B59B6", // Vibrant purple
                fillOpacity: 0.3,
                strokeColor: "transparent",
                strokeWeight: 0,
              }}
            />
            {/* Main marker - smaller, solid circle */}
            <Marker 
              position={driverCurrentLocation} 
              title="Driver Current Location" 
              label="🚗"
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 16,
                fillColor: "#9B59B6", // Vibrant purple
                fillOpacity: 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 3,
                labelOrigin: new window.google.maps.Point(0, 0)
              }}
            />
          </>
        )}
        {origin && <Marker position={origin} title="Driver Origin" label="O" />}
        {destination && <Marker position={destination} title="Driver Destination" label="D" />}
        {waypoints.map((wp, idx) => (
          <Marker
            key={`waypoint-${idx}`}
            position={wp}
            title={wp.label || `Waypoint ${idx + 1}`}
            label={wp.label === "Pickup" ? "P" : wp.label === "Dropoff" ? "X" : `${idx + 1}`}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,  // Increased from 8 to 12
              fillColor: wp.label === "Pickup" ? "#FFC000" : "#FF6B6B",
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,  // Increased from 2 to 3
              labelOrigin: new window.google.maps.Point(0, 0) // Center the label
            }}
          />
        ))}
        {midpoints.map((mp, idx) => (
          <Marker
            key={`midpoint-${idx}`}
            position={mp}
            title={`Midpoint ${idx + 1}`}
            label={`M${idx + 1}`}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,  // Increased from 6 to 10
              fillColor: "#4CAF50",
              fillOpacity: 0.8,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,  // Increased from 2 to 3
              labelOrigin: new window.google.maps.Point(0, 0) // Center the label
            }}
          />
        ))}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: { strokeColor: "#9B59B6", strokeWeight: 5, strokeOpacity: 0.85 },
              preserveViewport: false,
              draggable: false,
            }}
          />
        )}
      </GoogleMap>
      
      {/* Accurate Map Legend */}
      <div style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        padding: '12px',
        marginTop: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          fontWeight: 'bold',
          marginBottom: '8px',
          color: '#333',
          fontSize: '13px'
        }}>
          📍 Map Markers
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '8px',
          fontSize: '11px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              backgroundColor: '#E74C3C',
              borderRadius: '50% 50% 50% 0',
              transform: 'rotate(-45deg)',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '2px',
                left: '2px',
                width: '12px',
                height: '12px',
                backgroundColor: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'rotate(45deg)',
                fontSize: '8px',
                fontWeight: 'bold',
                color: '#E74C3C'
              }}>O</div>
            </div>
            <span style={{ color: '#555' }}>Origin (O)</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              backgroundColor: '#E74C3C',
              borderRadius: '50% 50% 50% 0',
              transform: 'rotate(-45deg)',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '2px',
                left: '2px',
                width: '12px',
                height: '12px',
                backgroundColor: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'rotate(45deg)',
                fontSize: '8px',
                fontWeight: 'bold',
                color: '#E74C3C'
              }}>D</div>
            </div>
            <span style={{ color: '#555' }}>Destination (D)</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              backgroundColor: '#FFC000',
              borderRadius: '50%',
              border: '2px solid #FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              color: '#333'
            }}>P</div>
            <span style={{ color: '#555' }}>Pickup (P)</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '16px',
              height: '16px',
              backgroundColor: '#FF6B6B',
              borderRadius: '50%',
              border: '2px solid #FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '9px',
              fontWeight: 'bold',
              color: 'white'
            }}>X</div>
            <span style={{ color: '#555' }}>Dropoff (X)</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: '#4CAF50',
              borderRadius: '50%',
              border: '2px solid #FFFFFF',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '7px',
              fontWeight: 'bold',
              color: 'white'
            }}>M</div>
            <span style={{ color: '#555' }}>Midpoint (M1, M2...)</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '3px',
              backgroundColor: '#9B59B6',
              borderRadius: '1px'
            }}></div>
            <span style={{ color: '#555' }}>Route Path</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideSharePassengerTripCardRoutePreview;
